import { supabase } from '../lib/supabase';

export const companyService = {
  async createCompany(companyData, fallbackUserId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const userId = user?.id || fallbackUserId;
      if (!userId) throw new Error('Not authenticated');

      // Use SECURITY DEFINER RPC function to bypass RLS session issues
      // This handles cases where email confirmation is enabled and session is not yet active
      const { data, error } = await supabase?.rpc('create_company_for_user', {
        p_user_id: userId,
        p_company_name: companyData?.companyName,
        p_registration_number: companyData?.registrationNumber?.trim() || null,
        p_address_line1: companyData?.addressLine1,
        p_address_line2: companyData?.addressLine2 || null,
        p_city: companyData?.city,
        p_county: companyData?.county || null,
        p_postcode: companyData?.postcode,
        p_contact_phone: companyData?.contactPhone,
        p_contact_email: companyData?.contactEmail
      });

      if (error) {
        // Detect duplicate registration number — only match the specific RPC error or constraint name.
        // Do NOT match generic 'duplicate key value violates unique constraint' — that catches
        // unrelated constraints (e.g. user_profiles_email_key) and mislabels the error.
        const msg = error?.message || '';
        if (
          msg?.includes('DUPLICATE_REGISTRATION_NUMBER') ||
          msg?.includes('companies_registration_number_key')
        ) {
          return {
            data: null,
            error: {
              message: 'This Companies House registration number is already registered. Please check your number and try again.',
              code: 'DUPLICATE_REGISTRATION_NUMBER'
            }
          };
        }
        throw error;
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getCompanyByAdminUser() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('companies')?.select('*')?.eq('admin_user_id', user?.id)?.maybeSingle();

      if (error) throw error;
      if (!data) {
        // Fallback: try getUserCompany for non-admin roles
        return await companyService?.getUserCompany();
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getUserCompany() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // PRIMARY PATH: Use SECURITY DEFINER RPC that bypasses RLS entirely.
      // This is the most reliable path — it checks all four possible links
      // (admin_user_id, user_profiles.company_id, company_users, workers)
      // without being blocked by RLS session timing issues.
      const { data: rpcCompany, error: rpcError } = await supabase
        ?.rpc('get_my_company');

      if (!rpcError && rpcCompany) return { data: rpcCompany, error: null };

      if (rpcError) {
        console.warn('[getUserCompany] RPC get_my_company failed, falling back to direct queries:', rpcError?.message);
      }

      // FALLBACK 1: Direct query — company where user is admin
      const { data: adminCompany } = await supabase
        ?.from('companies')
        ?.select('*, onboarding_finished')
        ?.eq('admin_user_id', user?.id)
        ?.maybeSingle();

      if (adminCompany) return { data: adminCompany, error: null };

      // FALLBACK 2: user_profiles.company_id (fast path for site_manager/worker)
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('company_id, companies(*, onboarding_finished)')
        ?.eq('id', user?.id)
        ?.maybeSingle();

      if (profile?.companies) return { data: profile?.companies, error: null };

      // FALLBACK 3: workers table
      const { data: workerData } = await supabase
        ?.from('workers')
        ?.select('company_id, companies(*, onboarding_finished)')
        ?.eq('user_profile_id', user?.id)
        ?.maybeSingle();

      if (workerData?.companies) return { data: workerData?.companies, error: null };

      // FALLBACK 4: company_users table (covers invited workers
      //    whose user_profiles.company_id may not have been set by the trigger)
      const { data: companyUserRow } = await supabase
        ?.from('company_users')
        ?.select('company_id, companies(*, onboarding_finished)')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (companyUserRow?.companies) return { data: companyUserRow?.companies, error: null };

      return { data: null, error: { message: 'No company found for this user' } };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async markOnboardingFinished(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('companies')
        ?.update({ onboarding_finished: true })
        ?.eq('id', companyId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getActiveSiteUsage(companyId) {
    try {
      const { data, error } = await supabase?.rpc('get_active_site_usage', {
        company_uuid: companyId
      });

      if (error) throw error;
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async canAddActiveSite(companyId) {
    try {
      const { data, error } = await supabase?.rpc('can_add_active_site', {
        company_uuid: companyId
      });

      if (error) throw error;
      return { data: data || false, error: null };
    } catch (error) {
      return { data: false, error: { message: error?.message } };
    }
  },


  /**
   * Safely delete a company account.
   * Cancels any active/trialing Stripe subscription before deleting the company row
   * and the admin auth user. If Stripe cancellation fails, deletion is aborted.
   * @param {string} companyId
   * @returns {{ data: { success: boolean } | null, error: { message: string } | null }}
   */
  async deleteCompany(companyId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const { data, error } = await supabase?.functions?.invoke('delete-company', {
        body: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      // Surface the exact error from the edge function body
      if (data?.error) throw new Error(data.error);
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  async updateRequirePhotoEvidence(companyId, value) {
    try {
      const { data, error } = await supabase
        ?.from('companies')
        ?.update({ require_photo_evidence: value })
        ?.eq('id', companyId)
        ?.select('id, require_photo_evidence')
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateCompanySettings(companyId, settings) {
    try {
      const { data, error } = await supabase
        ?.from('companies')
        ?.update(settings)
        ?.eq('id', companyId)
        ?.select('id, require_photo_evidence, require_photo_evidence_snag, require_toolbox_talk_signoff, enable_incident_reporting')
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

};
